import { usePostApi } from "../../../hooks";
import {
  setPermsProperty,
  setUserType,
} from "../../../redux/slices/permsSlice";
import {
  ILoginWarning,
  IPostLoginActions,
  IRotationModalProps,
  TLoginWarning,
} from "./login.model";
import { useDispatch } from "../../../redux/hooks";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { HANDOVER_SESSION_KEY, refreshLogoutTimer } from "../../../generator/utils/refreshLogoutTimer";
import { setVersionMessage } from "../../../redux/slices/preferencesSlice";

interface Props {
  setRotationModalProps: (props: IRotationModalProps) => void;
}
/**
 * Custom hook to handle user login functionality.
 *
 * @param setRotationModalProps - A function to set the properties of the rotation modal.
 * @returns An object containing:
 * - `handleLogin`: A function to handle the login process.
 * - `isLoading`: A boolean indicating whether the login request is in progress.
 * - `loginWarning`: An object containing login warning details, if any.
 * - `setLoginWarning`: A function to update the login warning state.
 * - `runPostLoginActions`: A function to execute post-login actions based on the user's page and type.
 */
const useLoginUser = ({ setRotationModalProps }: Props) => {
  const { post, isLoading } = usePostApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginWarning, setLoginWarning] = useState<TLoginWarning>(null);

  // Base download URL for apps
  const APP_DOWNLOAD_BASE_URL =
    "https://dev.ljelogbooks.com/api/v1/apps/DeskApp/";

  // Get appropriate download link based on platform
  const getDownloadLink = () => {
    // Check if we're on macOS
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    return isMac
      ? `${APP_DOWNLOAD_BASE_URL}eLogbooks-mac.dmg`
      : `${APP_DOWNLOAD_BASE_URL}eLogbooks-win.exe`;
  };

  const runPostLoginActions = ({ page, usertype }: IPostLoginActions) => {
    if (page === "audit2") {
      dispatch(
        setPermsProperty({
          key: "perms",
          value: [
            "uploadfiles",
            "downloadfiles",
            "deletefiles",
            "viewfiles",
            "viewhistory",
          ],
        }),
      );
      dispatch(setUserType(usertype));
      navigate(usertype === 9 ? "/summary/reviews" : "/summary/audits");
    } else if (
      page === "management" &&
      (usertype === 1 || usertype === 4 || usertype === 5)
    ) {
      dispatch(setUserType(usertype));
      navigate("/management");
    } else {
      navigate("/home");
    }
  };

  const handleLogin = async (params: any) => {
    await post({
      request: "login",
      body: { params },
      onSuccess: res => {
        if (res.data?.multi && res.data?.list) {
          setRotationModalProps({
            open: true,
            list: res.data.list,
            params,
          });
          return;
        }

        const { _userid, _session, _yachtid, _code } = res;
        localStorage.setItem(
          "authData",
          JSON.stringify({ _userid, _session, _yachtid, _code }),
        );
        localStorage.removeItem(HANDOVER_SESSION_KEY);
        refreshLogoutTimer();

        if (window?.api && res?.message && res?.link) {
          setLoginWarning({
            message: res.message,
            link: getDownloadLink(),
            page: res.data.page,
            usertype: res.data.usertype,
          });
          dispatch(
            setVersionMessage({
              warning: true,
              message:
                "You're using an outdated version of the desktop app. Please uninstall it and download the latest version from Settings",
            }),
          );
          return;
        }
        if (window?.api)
          dispatch(
            setVersionMessage({
              message:
                "You are running the most up-to-date version of the desktop app",
            }),
          );

        runPostLoginActions({
          page: res.data?.page,
          usertype: res.data?.usertype,
        });
      },
      onError: res => {
        if (res?.denied && res?.link) {
          setLoginWarning({
            message: res.error,
            link: getDownloadLink(), // Use platform-specific link instead of res.link
            denied: true,
          });
        }
      },
    });
  };
  return {
    handleLogin,
    isLoading,
    loginWarning,
    setLoginWarning,
    runPostLoginActions,
  };
};

export default useLoginUser;
